<template>
  <div>
    <div class="flex justify-between items-start gap-4">
      <div class="lg:w-full">
        <header class="font-bold">Visible to Agent</header>
        <p class="text-sm text-gray-500 mb-8">This will be sent to the agent after you save it.</p>

        <div v-if="customFieldsForEntity.length == 0">
          <QuillInput
            v-model="newPipelineReview.intro_question"
            label="How is the agent and how is their week going so far? Is there anything we can do to help the agent on a personal level?"
            name="intro_question"
          />

          <QuillInput
            v-model="newPipelineReview.top_three_prospects"
            label="Who are the agent's top 3 customers, and what could the agent be doing differently to get each of them into escrow?"
            name="top_three_prospects"
          />

          <QuillInput
            v-model="newPipelineReview.team_leader_suggestions"
            label="What is the agent not getting from us that would help them do their job at the highest possible level?"
            name="team_leader_suggestions"
          />
        </div>
        <div v-else>
          <div v-for="customField in customFieldsForEntity" :key="customField.id" class="sm:col-span-1 mt-4">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{ customField.name }}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900">
              <ValueViewerAndEditor
                :custom-field="customField"
                :custom-field-value="newPipelineReview.customFields.find(f => f.custom_field_id === customField.id)"
                custom-fieldable-type="PipelineReview"
                :custom-fieldable-id="1"
                :method="'edit'"
                @updated="handleCustomFieldUpdate($event)"
              />
            </dd>
          </div>
        </div>

        <hr class="my-8 -ml-8 -mr-8" />

        <header class="font-bold">Private</header>
        <p class="text-sm text-gray-500 mb-8">This is not visible to the agent.</p>

        <QuillInput
          v-model="newPipelineReview.team_leader_private_notes"
          label="Your Private Notes"
          name="team_leader_private_notes"
        />

        <label class="mt-4 block text-sm leading-5 font-medium text-gray-700 mb-1">Pipeline Performance</label>
        <PipelinePerformance :score="newPipelineReview.score" @set-score="score => (newPipelineReview.score = score)" />

        <TButton
          label="Save pipeline review"
          color="amber"
          :disabled="!validPipelineReview"
          class="mt-8"
          @click="createPipelineReview"
        >
          <LocalLoadingSpinner />
        </TButton>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../Layout/index.vue';
import Card from '../Card/index.vue';
import MenuTabs from '../shared/MenuTabs.vue';
import Progress from '../shared/Progress.vue';
import PipelinePerformance from '../PipelinePerformance/index.vue';
import QuillInput from '../shared/QuillInput.vue';
import ValueViewerAndEditor from '../custom_fields/ValueViewerAndEditor.vue';

export default {
  components: {
    Layout,
    Card,
    PipelinePerformance,
    QuillInput,
    ValueViewerAndEditor,
    MenuTabs,
    Progress,
  },
  props: {
    teammate: {
      type: Object,
      required: true,
    },
    contactEvent: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      loaded: false,
      pipelineReviews: [],
      newPipelineReview: {
        intro_question: '',
        top_three_prospects: '',
        team_leader_suggestions: '',
        team_leader_private_notes: '',
        score: null,
        files: [],
        customFields: [],
      },
      customFieldsForEntity: [],
      eventRecording: null,
      selectedRecordingTab: 'Score',
    };
  },
  computed: {
    validPipelineReview() {
      if (this.customFieldsForEntity.length == 0) {
        return (
          this.newPipelineReview.intro_question &&
          this.newPipelineReview.top_three_prospects &&
          this.newPipelineReview.team_leader_suggestions &&
          this.newPipelineReview.score
        );
      } else {
        return true;
      }
    },
    previousPipelineReview() {
      return this.pipelineReviews[0];
    },
  },
  async mounted() {
    await this.fetchCustomFieldsForEntity();
    await this.fetchPipelineReviews();
    await this.fetchEventForPipelineReview();

    this.loaded = true;
  },
  methods: {
    async fetchCustomFieldsForEntity() {
      const res = await fetch(`/api/contacts/${this.teammate.id}/pipeline_reviews/custom_fields_for_entity`);
      const data = await res.json();
      this.customFieldsForEntity = data.custom_fields_for_entity;
    },
    async fetchPipelineReviews() {
      const res = await fetch(`/api/contacts/${this.teammate.id}/pipeline_reviews`);
      const data = await res.json();

      this.pipelineReviews = data;
    },
    async fetchEventForPipelineReview() {
      const res = await fetch(`/api/contacts/${this.teammate.id}/pipeline_reviews/contact_event`);
      const data = await res.json();

      this.eventRecording = data.record ? (data.record.event_recording ? data.record.event_recording : null) : null;
    },
    async createPipelineReview() {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      let formData = new FormData();
      formData.append('intro_question', self.newPipelineReview.intro_question);
      formData.append('top_three_prospects', self.newPipelineReview.top_three_prospects);
      formData.append('team_leader_suggestions', self.newPipelineReview.team_leader_suggestions);
      formData.append('team_leader_private_notes', self.newPipelineReview.team_leader_private_notes);
      formData.append('score', self.newPipelineReview.score);
      formData.append('custom_fields_and_values[]', JSON.stringify(self.newPipelineReview.customFields));
      formData.append('contact_event_id', self.contactEvent.id);
      for (let i = 0; i < self.newPipelineReview.files.length; i++) {
        formData.append('files[]', self.newPipelineReview.files[i], self.newPipelineReview.files[i].name);
      }
      const res = await fetch(`/api/contacts/${this.teammate.id}/pipeline_reviews`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      });

      const data = await res.json();
      self.$store.commit('general/hideLocalLoadingSpinner');
      if (data.status != 'error') {
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Pipeline Review Created',
          alertDescription:
            'Your pipeline review was successfully created.  Use the button below to dismiss this alert.',
        });
        await this.$router.go();
      } else {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Pipeline Review Error',
          alertDescription: data.message,
        });
      }
    },
    handleCustomFieldUpdate(e) {
      this.newPipelineReview.customFields.push(e);
    },
  },
};
</script>
